import React, {
  ComponentProps,
  FC,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { TaskIdV2 } from 'api/models';
import { Action } from 'components/molecules/action-footer';
import {
  ActionPanel,
  usePopoverActionRef,
} from 'components/molecules/action-panel';
import { useTranslation } from 'hooks/use-translation';
import { queueMacrotask } from 'utils/macrotask';
import { DeletePopover, DeletePopoverAction } from './components';

const { PopoverAction } = ActionPanel;

interface Props {
  ids: string[];
  onDelete: (ids: TaskIdV2[]) => void;
  onClose: () => void;
  title: ReactNode;
  allowed: ComponentProps<typeof PopoverAction>['allowed'];
}

export const Delete: FC<Props> = ({
  onDelete,
  onClose,
  ids,
  title,
  allowed,
}) => {
  const confirmationText = useTranslation(
    'task_list.bulk_editor.options.delete.confirm',
    { count: ids.length, b: (chunks) => <b>{chunks}</b> },
  );
  const innerRef = usePopoverActionRef();

  const onDeleteSelected = useCallback(() => {
    innerRef.current?.hide();
    onClose();
    queueMacrotask(() =>
      onDelete(ids.map((x) => parseInt(x, 10)).map(TaskIdV2)),
    );
  }, [onDelete, ids, onClose, innerRef]);

  const deleteSaveTitle = useTranslation(
    'task_list.bulk_editor.options.delete.save',
  );
  const primary = useMemo(
    () =>
      ({
        title: deleteSaveTitle,
        onClick: onDeleteSelected,
        danger: true,
        type: 'text',
      } as Action),
    [deleteSaveTitle, onDeleteSelected],
  );

  const cancelTitle = useTranslation('task_list.bulk_editor.cancel');
  const cancel = useMemo(
    () =>
      ({
        title: cancelTitle,
        onClick: () => innerRef?.current?.hide(),
      } as Action),
    [innerRef, cancelTitle],
  );

  return (
    <DeletePopoverAction ref={innerRef} allowed={allowed} title={title}>
      <DeletePopover
        title={title}
        confirmationText={confirmationText}
        ok={primary}
        cancel={cancel}
      />
    </DeletePopoverAction>
  );
};
